import apiClient from './api.ts';
import {
    AssignCourseTeacherSchema,
    ChangeClassCourseToTeacherSchema,
    RegisterTeacherSchema,
    TeacherCompetencyAssignment,
    TeacherInputSchema
} from "@/types/teacher_schema.ts";

export default {
    async getAllTeachers(isActive: boolean, search: string, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc', searchField: string = '', classId?: number, gradeId?: number) {
        const params = new URLSearchParams({
            is_active: isActive.toString(),
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        if (search) params.set('search', search);
        if (search && searchField) params.set('search_field', searchField);
        if (classId) params.set('class_id', classId.toString());
        if (gradeId) params.set('grade_id', gradeId.toString());
        return await apiClient.get(`teachers?${params}`);
    },
    async getTeacherById(
        teacherId: number,
        options?: {
            academic_year_id?: number;
            include_assignments?: boolean;
        }
    ) {
        const params = new URLSearchParams();

        if (options?.academic_year_id) {
            params.append('academic_year_id', options.academic_year_id.toString());
        }

        if (options?.include_assignments !== undefined) {
            params.append('include_assignments', options.include_assignments.toString());
        }

        const queryString = params.toString();
        return await apiClient.get(`teachers/${teacherId}${queryString ? `?${queryString}` : ''}`);
    },
    async getTeacherByCourseId(courseId: number, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc') {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sort,
            order
        });
        return await apiClient.get(`teachers/course/${courseId}?${params}`);
    },
    async getAllTeachersNonCourse(isActive: boolean) {
        return await apiClient.get(`teachers/non-course?is_active=${isActive}`);
    },
    async createTeacher(Teacher: RegisterTeacherSchema) {
        return await apiClient.post(`/auth/register/teacher`, Teacher);
    },
    async createTeacherEnhanced(Teacher: RegisterTeacherSchema) {
        return await apiClient.post(`/auth/register/teacher`, Teacher);
    },
    async validateTeachersImport(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return await apiClient.post(`/teachers/import/validate`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            validateStatus: (status) => status === 200 || status === 400 // Accept 400 for validation errors
        });
    },
    async importTeachers(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return await apiClient.post(`/teachers/import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    async validateTeachers(Teachers: RegisterTeacherSchema[]) {
        return await apiClient.post(`/auth/register/teachers/validate`, Teachers);
    },
    async createTeachers(Teachers: RegisterTeacherSchema[]) {
        return await apiClient.post(`/auth/register/teachers`, Teachers);
    },
    async updateTeacher(TeacherId: number, Teacher: TeacherInputSchema) {
        return await apiClient.put(`teachers/${TeacherId}`, Teacher);
    },
    async deleteTeacher(TeacherId: number) {
        return await apiClient.delete(`teachers/${TeacherId}`);
    },
    async archiveTeacher(TeacherId: number, nonCourse: boolean) {
        return await apiClient.put(`teachers/${TeacherId}/non-course/${nonCourse}/archive`);
    },
    async unarchiveTeacher(TeacherId: number) {
        return await apiClient.put(`teachers/${TeacherId}/unarchive`);
    },
    async assignCourseToTeacher(classId: number, courseId: number, teacherId: number, addAsSpecialization: boolean = false, preferredSpecialization: boolean = false, experienceYears: number = 0, proficiencyLevel: string = 'Basic', academicYearId?: number) {
        const params = new URLSearchParams();
        if (academicYearId) {
            params.set('academic_year_id', academicYearId.toString());
        }
        const queryString = params.toString();

        return await apiClient.post(`teacher-course/assign${queryString ? `?${queryString}` : ''}`, { class_id: classId, course_id: courseId, teacher_id: teacherId, add_as_specialization: addAsSpecialization, preferred_specialization: preferredSpecialization, experience_years: experienceYears, proficiency_level: proficiencyLevel })
    },
    async unassignClassCourseToTeacher(classCourseId: number, teacherId: number) {
        return await apiClient.post('teacher-course/unassign', { class_course_id: classCourseId, teacher_id: teacherId })
    },
    async assignCourseToTeachers(assignments: AssignCourseTeacherSchema[]) {
        return await apiClient.post('teacher-course/assigns', assignments)
    },
    async changeClassCourseToTeachers(change: ChangeClassCourseToTeacherSchema) {
        return await apiClient.put(`teachers/change-course`, change)
    },
    async uploadProfile(teacherId: number, data: FormData) {
        return await apiClient.post(`teachers/${teacherId}/profile/upload`, data, { headers: {
            "Content-Type": "multipart/form-data"
        } });
    },
    async downloadExcelTemplate(count: number = 10, autoGenerateUsername: boolean = true, autoGeneratePass: boolean = true, lang: string = 'en') {
        const params = new URLSearchParams({
            count: count.toString(),
            autoGenerateUsername: autoGenerateUsername.toString(),
            autoGeneratePass: autoGeneratePass.toString(),
            lang: lang,
            templateType: 'optionB'
        });

        return await apiClient.get(`teachers/template/excel?${params}`, {
            responseType: 'blob',
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        });
    },

    async updatePhoneNumberTeacher(teacherId: number, userId: number, oldPhoneNumber: string, phoneNumber: string) {
        const params = new URLSearchParams({ oldPhoneNumber, phoneNumber });
        return await apiClient.put(`teachers/${teacherId}/user/${userId}/phone-number?${params}`);
    },
    async resetPassword(userId: number, newPassword: string) {
        return await apiClient.post(`users/${userId}/reset-password`, { new_password: newPassword });
    },

    // Teacher grade-course competency (teacher_grade_courses table)
    async assignTeacherCompetencies(assignments: TeacherCompetencyAssignment[]) {
        return await apiClient.post('teacher-course/competencies', assignments);
    },
    async getTeacherCompetencies(academicYearId: number, page: number = 1, limit: number = 20) {
        return await apiClient.get(`teacher-course/competencies?academic_year_id=${academicYearId}&limit=${limit}&offset=${page}`);
    },
    async unassignTeacherCompetency(assignment: TeacherCompetencyAssignment) {
        return await apiClient.delete('teacher-course/competencies', { data: assignment });
    },
};
